import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/aaa-api-client";
import { validateRequiredString } from "@bfc-software/bfc-web-ui/lib/search-params";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { Form as ReactAriaForm } from "react-aria-components";
import { TextInput, Button, Heading, toast } from "@bfc-software/pallet";

import AuthContainer from "./-components/auth-container";
import styles from "./reset-password.module.css";
import LoginCard from "./-components/login-card";

type QueryParams = {
  token?: string;
};

type ParsedQueryParams = {
  token: string;
};

export const Route = createFileRoute("/$lang/_layout/auth/reset-password")({
  component: ResetPassword,
  validateSearch: (search: QueryParams): ParsedQueryParams => {
    return {
      token: validateRequiredString({ val: search.token }),
    };
  },
});

function extractErrorType(
  errorMessage: string | undefined = "",
): string | null {
  // Use a regex to match "type: some_value," or "type: some_value "
  const match = errorMessage.match(/type:\s*(\w+)/);
  return match ? match[1] : "default";
}

function ResetPassword() {
  const params = Route.useParams();
  const searchParams = Route.useSearch();
  const router = useRouter();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  // Derived state: do they match?
  const passwordsMatch =
    password && confirmPassword && password === confirmPassword;
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.resetPassword,
  });

  useEffect(() => {
    // if either password field is empty, clear the error
    if (!password || !confirmPassword) {
      setError("");
      return;
    }

    // if both are filled but don't match, set an error
    if (password !== confirmPassword) {
      setError(t("passwordsNotMatchingError"));
    } else {
      // if they match, clear the error
      setError("");
    }
  }, [password, confirmPassword, t]);

  const { mutate, isPending } = apiClient().useMutation(
    "post",
    "/auth/reset-pwd",
  );

  const resetPassword = useCallback(async () => {
    mutate(
      {
        body: {
          password: password,
          reset_token: searchParams.token,
        },
      },
      {
        onSuccess: () => {
          toast.success(t("resetSuccessfully"));
          router.navigate({
            to: "/$lang/auth/login",
            params: { lang: params.lang },
          });
        },
        onError: (error) => {
          if (error) {
            const errorType = extractErrorType(error.message);
            let errorMessage: string;
            switch (errorType) {
              case "magic_link_not_found": {
                errorMessage = t("linkError");
                break;
              }
              case "unable_to_auth_password_reset_token": {
                errorMessage = t("linkError");
                break;
              }
              case "weak_password": {
                errorMessage = t("weakPasswordError");
                break;
              }
              default: {
                errorMessage = t("unknownError");
                break;
              }
            }
            setError(errorMessage);
            toast.error(
              <div className={styles.errorToast}>
                <p className={styles.heading}>{t("resetError")}</p>
                <p className={styles.errorMessage}>{errorMessage}</p>
              </div>,
            );
          }
        },
      },
    );
  }, [mutate, password, t, searchParams.token, router, params.lang]);

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    resetPassword();
  }

  return (
    <AuthContainer>
      <LoginCard data-testid="reset-password-entry">
        <Heading text={t("title")} level="1" />
        <ReactAriaForm onSubmit={handleSubmit} className={styles.form}>
          <TextInput
            id="reset-password-input"
            className={styles.resetPasswordInput}
            type="password"
            label={t("resetPassword")}
            name="resetPassword"
            autofocus={true}
            onChange={(ev) => setPassword(ev.target.value)}
            isRequired={true}
          />
          <TextInput
            id="confirm-reset-password-input"
            className={styles.resetPasswordInput}
            type="password"
            label={t("resetPasswordConfirm")}
            name="resetPasswordConfirm"
            autofocus={false}
            onChange={(ev) => setConfirmPassword(ev.target.value)}
            isRequired={true}
            errorMessage={error}
          />
          <div className={styles.signInOptions}>
            <div className={styles.submitButton}>
              <Button
                isDisabled={isPending || !passwordsMatch}
                data-method="reset-password"
                text={t("submitButton")}
                type="submit"
              />
            </div>
          </div>
        </ReactAriaForm>
      </LoginCard>
    </AuthContainer>
  );
}

export default ResetPassword;
